import { AfterContentInit, AfterViewChecked, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { QuoteModel } from '@bytel/bytel-sales';
import { CheckoutStorageService, UserService } from '@bytel/checkout';

import { AppService } from '@services/app.services';
import { EventsService } from '@services/events.service';
import { Ftth3ClicsService } from '@services/ftth3-clics.service';
import { MainCartService } from '@services/main-cart.service';
import { MessageService } from '@services/message.service';
import { CheckoutOauth2ResourcesService } from '@services/oauth2-ressources.service';
import { StepperService } from '@services/stepper.service';
import { TagGeneratorServices } from '@services/tag-generator.services';

import { MixedCartComponent } from '@tunnel/cart/mixed-cart/mixed-cart.component';
import { TunnelComponent } from '@tunnel/tunnel.component';

import { TagService } from '@shared/tag/tag.service';

import { Observable, of, throwError } from 'rxjs';

import { EmptyCartItemComponent } from './empty-cart-item/empty-cart-item.component';
import { EmptyCartItemHorizontalComponent } from './empty-cart-item/horizontal/empty-cart-item-horizontal.component';

@Component({
    selector: 'checkout-cart',
    templateUrl: './cart.component.html',
    styleUrl: 'cart.component.scss',
    standalone: true,
    imports: [EmptyCartItemComponent, EmptyCartItemHorizontalComponent, ReactiveFormsModule, MixedCartComponent],
})
export class CartComponent extends TunnelComponent implements OnInit, AfterContentInit, AfterViewChecked {
    public form: FormGroup;
    public isCartEmpty: boolean = true;
    public page: string = 'panier_1';
    public productLoaded: boolean;
    public quotesInCart: QuoteModel[];

    public constructor(
        // NOSONAR
        protected stepperService: StepperService,
        protected cartService: MainCartService,
        public appService: AppService,
        protected messageService: MessageService,
        private formBuilder: FormBuilder,
        protected checkoutTagsServices: TagGeneratorServices,
        protected tagService: TagService,
        protected oauth2Resource: CheckoutOauth2ResourcesService,
        protected userService: UserService,
        private cd: ChangeDetectorRef,
        protected eventsService: EventsService,
        protected checkoutStorage: CheckoutStorageService,
        protected ftth3ClicsService: Ftth3ClicsService,
    ) {
        super(
            stepperService,
            messageService,
            cartService,
            checkoutTagsServices,
            tagService,
            appService,
            userService,
            oauth2Resource,
            eventsService,
        );
    }

    public ngAfterContentInit(): void {
        this.cd.detectChanges();
    }

    public ngAfterViewChecked(): void {
        if (this.productLoaded === false) {
            // the setTimeout is not a hack but just a way to delay the function execution for the next
            // javascript thick (move down to the stack) and avoid the definition on random time.
            this.productLoaded = true;
        }
    }

    public ngOnInit(): void {
        super.ngOnInit();

        this.form = this.formBuilder.group({});
        this.quotesInCart = this.cartService.cartModel.quotes;
    }

    public setAdditionnalTags(): void {
        this.additionnalTags = {
            pageVariante: {
                panierVide: this.cartService.cartModel.isCartEmpty(),
            },
        };
    }

    public validate(): Observable<boolean> {
        this.isSubmitted = true;
        try {
            if (!this.form.valid) {
                throw new Error('Merci de renseigner toutes les informations');
            }
            this.cartService.getQuote().checkIsValid();
            this._checkPortabilityAndUpdateFtth3Clics();
        } catch (e) {
            return throwError({
                message: e.message,
                level: MessageService.ERRORS_LEVELS.warning,
            });
        }
        if (this.cartService.getQuote(1)?.products.length === 0) {
            this.cartService.removeQuote(1);
        }
        return of(true);
    }

    public initStep(): void {
        this._refresh();

        this.cartService.cartQuoteSubscription.subscribe(() => {
            this._refresh();
        });
    }

    protected setDynamicRecallParam(): void {
        const recallId = this.cartService.getQuote().isAcquisitionFix() ? 'tlvctcfai3' : 'tlvctcmobile2';
        this.recallService.setMetaRecall(recallId);
    }

    private _checkPortabilityAndUpdateFtth3Clics(): void {
        if (!!this.form.get('portability')?.value?.checked) {
            this.ftth3ClicsService.stopUsingFtth3Clics();
            this.stepperService.refresh();
        }
    }

    private _refresh(): void {
        const quote: QuoteModel = this.cartService.getQuote();
        this.quotesInCart = this.cartService.cartModel.quotes;
        this.isCartEmpty = this.cartService.cartModel.isCartEmpty();
        this.isPreorder = quote.isPreorder();
        this.productLoaded = false;
    }
}
